export interface LayerRect {
  top: number
  left: number
  width: number
  height: number
}

export interface LayerSize {
  width: number
  height: number
}

export interface Viewport {
  width: number
  height: number
}

export interface LayerMargins {
  top: number
  right: number
  bottom: number
  left: number
}

const NO_MARGINS: LayerMargins = { top: 0, right: 0, bottom: 0, left: 0 }

export interface ComputeLayerPositionOptions {
  anchor: LayerRect
  layer: LayerSize
  viewport: Viewport
  positionArea: string | null
  /** The layer's own writing direction. Astryx's `self-*` areas resolve against it. */
  direction?: 'ltr' | 'rtl'
  /**
   * The layer's own margins. Astryx layers carry a small margin as the gap between the
   * surface and its trigger, and `position-area` lines up the *margin* box -- so the
   * margins are part of the geometry, not something to strip off.
   */
  margins?: LayerMargins
}

export interface LayerPosition {
  top: number
  left: number
}

/** Which side of the anchor the layer sits on. */
type Side = 'block-start' | 'block-end' | 'inline-start' | 'inline-end'

/** How the layer lines up along the axis it does not occupy. */
type Align = 'start' | 'center' | 'end'

interface Placement {
  side: Side
  align: Align
}

/**
 * Read the `position-area` value Astryx's `useLayer` writes onto a layer back into a
 * side + alignment pair. Astryx only ever emits the `self-*` keyword family (see
 * `getPositionArea` in @astryxdesign/core), so that is all this understands:
 *
 *   self-block-end span-self-inline-end  ->  below the anchor, left edges flush
 *   self-block-start                     ->  above the anchor, horizontally centered
 *   self-inline-end span-self-block-start->  right of the anchor, bottom edges flush
 *
 * The `span-*` keyword names the direction the layer is allowed to grow into, which is
 * the opposite end from the edge it lines up with -- `span-self-inline-end` grows to the
 * right, so the layer's *left* edge is the one pinned to the anchor.
 */
function parsePositionArea(positionArea: string | null): Placement {
  const tokens = (positionArea ?? '').trim().split(/\s+/).filter(Boolean)

  let side: Side = 'block-start'
  let align: Align = 'center'

  for (const token of tokens) {
    switch (token) {
      case 'self-block-start':
        side = 'block-start'
        break
      case 'self-block-end':
        side = 'block-end'
        break
      case 'self-inline-start':
        side = 'inline-start'
        break
      case 'self-inline-end':
        side = 'inline-end'
        break
      case 'span-self-block-end':
      case 'span-self-inline-end':
        align = 'start'
        break
      case 'span-self-block-start':
      case 'span-self-inline-start':
        align = 'end'
        break
      default:
        break
    }
  }

  return { side, align }
}

function alignAlong(
  align: Align,
  anchorStart: number,
  anchorExtent: number,
  layerExtent: number
): number {
  if (align === 'start') return anchorStart
  if (align === 'end') return anchorStart + anchorExtent - layerExtent
  return anchorStart + (anchorExtent - layerExtent) / 2
}

/**
 * Resolve the logical placement to physical left/right. Only the inline axis flips: a
 * block-placed layer mirrors its *alignment* (which runs along the inline axis), an
 * inline-placed layer mirrors the *side* it sits on and keeps its block alignment.
 */
function resolveDirection(placement: Placement, direction: 'ltr' | 'rtl'): Placement {
  if (direction !== 'rtl') return placement

  if (placement.side === 'inline-start' || placement.side === 'inline-end') {
    return { side: flipSide(placement.side), align: placement.align }
  }
  return { side: placement.side, align: flipAlign(placement.align) }
}

function place(placement: Placement, anchor: LayerRect, layer: LayerSize): LayerPosition {
  const { side, align } = placement

  if (side === 'block-start' || side === 'block-end') {
    const top = side === 'block-start' ? anchor.top - layer.height : anchor.top + anchor.height
    return { top, left: alignAlong(align, anchor.left, anchor.width, layer.width) }
  }

  const left = side === 'inline-start' ? anchor.left - layer.width : anchor.left + anchor.width
  return { top: alignAlong(align, anchor.top, anchor.height, layer.height), left }
}

function flipSide(side: Side): Side {
  switch (side) {
    case 'block-start':
      return 'block-end'
    case 'block-end':
      return 'block-start'
    case 'inline-start':
      return 'inline-end'
    case 'inline-end':
      return 'inline-start'
  }
}

/** A centered layer has nothing to flip to -- flipping it lands on itself. */
function flipAlign(align: Align): Align {
  if (align === 'start') return 'end'
  if (align === 'end') return 'start'
  return 'center'
}

function fitsInViewport(position: LayerPosition, layer: LayerSize, viewport: Viewport): boolean {
  return (
    position.top >= 0 &&
    position.left >= 0 &&
    position.top + layer.height <= viewport.height &&
    position.left + layer.width <= viewport.width
  )
}

function clamp(value: number, max: number): number {
  return Math.min(Math.max(value, 0), Math.max(max, 0))
}

export function computeLayerPosition(options: ComputeLayerPositionOptions): LayerPosition {
  const { anchor, layer, viewport, positionArea, direction = 'ltr', margins = NO_MARGINS } = options

  // Everything below works in margin-box terms, which is both what `position-area`
  // aligns and what an inline `top`/`left` on an out-of-flow box addresses -- the
  // browser adds the margins back on top of the values returned here.
  const marginBox: LayerSize = {
    width: layer.width + margins.left + margins.right,
    height: layer.height + margins.top + margins.bottom
  }

  // `useLayer`'s `positioning: 'custom'` mode emits no position-area: the consumer
  // authors its own `anchor()` insets, and every such consumer in Astryx pins the
  // layer's start corner to the anchor's (e.g. Tokenizer's expand-in-place layer).
  if (!positionArea || !positionArea.trim()) {
    return {
      top: clamp(anchor.top, viewport.height - marginBox.height),
      left: clamp(anchor.left, viewport.width - marginBox.width)
    }
  }

  const placement = resolveDirection(parsePositionArea(positionArea), direction)

  // Stands in for the `position-try-fallbacks: flip-block, flip-inline, flip-block
  // flip-inline` that Astryx hands the browser: try the requested placement, then the
  // same placement mirrored across each axis, and take the first that fits on screen.
  const candidates: Placement[] = [
    placement,
    { side: flipSide(placement.side), align: placement.align },
    { side: placement.side, align: flipAlign(placement.align) },
    { side: flipSide(placement.side), align: flipAlign(placement.align) }
  ]

  for (const candidate of candidates) {
    const position = place(candidate, anchor, marginBox)
    if (fitsInViewport(position, marginBox, viewport)) {
      return position
    }
  }

  // Nothing fits (a layer taller or wider than the viewport, or an anchor pinned to an
  // edge). Keep the requested placement and pull it back on screen so it stays usable.
  const fallback = place(placement, anchor, marginBox)
  return {
    top: clamp(fallback.top, viewport.height - marginBox.height),
    left: clamp(fallback.left, viewport.width - marginBox.width)
  }
}
